import React from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';
import ParticlesBackground from './ParticlesBackground';
import AppNavbar from './Navbar';
import './Momentum.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const buildSignalsSrc = (locationState) => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('ticker') || locationState?.ticker || '';
    const c = params.get('compare') || locationState?.compare || 'QQQ';
    const base = (API_URL || '').replace(/\/$/, '');
    const qs = t ? `?ticker=${encodeURIComponent(t)}&compare=${encodeURIComponent(c)}` : '';
    return `${base}/data/signals${qs}`;
};

const SignalsDashboard = () => {
    const { isAuthenticated, loading, error, setError, login } = useAuth();
    const [localEmail, setLocalEmail] = React.useState('');
    const [iframeLoaded, setIframeLoaded] = React.useState(false);
    const [iframeError, setIframeError] = React.useState(false);
    const [viewportWidth, setViewportWidth] = React.useState(window.innerWidth);
    const iframeRef = React.useRef(null);
    const iframeLoadedRef = React.useRef(false);
    const location = useLocation();

    React.useEffect(() => {
        if (!isAuthenticated) return;
        iframeLoadedRef.current = false;
        setIframeLoaded(false);
        setIframeError(false);
        const timer = setTimeout(() => {
            if (!iframeLoadedRef.current) setIframeError(true);
        }, 60000);
        return () => clearTimeout(timer);
    }, [isAuthenticated]);

    // Re-fit on resize — without this, a desktop window resized mid-session after
    // load would keep whatever scale was computed on first render (2026-07-23 fix,
    // see DASH_W comment below for the underlying bug this closes).
    React.useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        await login(localEmail);
    };

    if (loading) {
        return (
            <div className="momentum-page">
                <header className="momentum-header">
                    <AppNavbar />
                    <ParticlesBackground />
                    <div className="header-content"><h1>Signals</h1></div>
                </header>
                <main className="momentum-main">
                    <div className="container"><p>Loading...</p></div>
                </main>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="momentum-page">
                <header className="momentum-header">
                    <AppNavbar />
                    <ParticlesBackground />
                    <div className="header-content"><h1>Signals</h1></div>
                </header>
                <main className="momentum-main">
                    <div className="container">
                        <div className="auth-section">
                            <h2>Subscriber Access Required</h2>
                            <p>Enter your Substack email to access the signals dashboard.</p>
                            <form onSubmit={handleSubmit}>
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    value={localEmail}
                                    onChange={(e) => setLocalEmail(e.target.value)}
                                    required
                                />
                                <button type="submit">Login</button>
                            </form>
                            {error && <p className="error-message">{error}</p>}
                            <iframe
                                src="https://machinaquanta.substack.com/embed"
                                title="Substack Subscription"
                                style={{ width: '100%', maxWidth: '480px', height: '320px', border: 'none' }}
                                scrolling="no"
                            />
                        </div>
                    </div>
                </main>
                <footer className="models-footer">
                    <div className="container">
                        <p>&copy; 2023-{new Date().getFullYear()} machinaquanta.com. All rights reserved.</p>
                    </div>
                </footer>
            </div>
        );
    }

    // DASH_W matches dark_pool_template.html's #dashboard fixed width (1325px — that
    // page's internal layout doesn't reflow). Fixed 2026-07-23: this used to only
    // scale-to-fit under 800px (isMobile), assuming any desktop window was wide
    // enough — but plenty of laptop windows (unmaximized, 13" screens, split-screen)
    // are narrower than 1325px too, so the iframe was rendering at width:100% with
    // no scaling while its content still wanted 1325px, producing an internal
    // horizontal scrollbar INSIDE the iframe rather than a properly fit view (user-
    // reported: "laptop window should just show the window without needing to
    // scroll"). Also DASH_W was previously 1650, which never matched the template's
    // actual width at all. Now: scale to fit ANY viewport narrower than the content,
    // uniformly, with a floor so a narrow phone doesn't compress to unreadable.
    const DASH_W = 1325;
    const DASH_H = 1100;
    const MIN_SCALE = 0.40;
    const scale = Math.min(1, Math.max(viewportWidth / DASH_W, MIN_SCALE));
    const isScaled = scale < 1;
    const scaledW = Math.ceil(DASH_W * scale);
    const scaledH = Math.ceil(DASH_H * scale);
    const containerHeight = isScaled ? scaledH : DASH_H;
    const needsHScroll = isScaled && scaledW > viewportWidth;
    const iframeSrc = buildSignalsSrc(location.state);

    return (
        <div className="momentum-page">
            <header className="momentum-header">
                <AppNavbar />
                <ParticlesBackground />
                <div className="header-content">
                    <h1>Signals</h1>
                </div>
            </header>

            <main className="momentum-main" style={{ padding: '12px 0 0 0' }}>
                <div className="container" style={{ maxWidth: '100%', padding: '0', position: 'relative' }}>
                    {/* Wrapper clips height; allows horizontal scroll when scaled width > viewport */}
                    <div style={{ width: '100%', height: `${containerHeight}px`, overflowX: needsHScroll ? 'auto' : 'hidden', overflowY: 'hidden', position: 'relative' }}>
                        {!iframeLoaded && !iframeError && (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0,
                                height: '100%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(10,10,20,0.85)', zIndex: 2,
                                color: '#aaa', fontSize: '14px',
                            }}>
                                Loading signals…
                            </div>
                        )}
                        {iframeError && (
                            <div style={{
                                height: '100%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(10,10,20,0.85)',
                                color: '#f66', fontSize: '14px', flexDirection: 'column', gap: '12px',
                            }}>
                                <span>Signals dashboard timed out.</span>
                                <button onClick={() => { setIframeLoaded(false); setIframeError(false); if (iframeRef.current) iframeRef.current.src = iframeSrc; }}
                                    style={{ padding: '6px 16px', cursor: 'pointer' }}>Retry</button>
                            </div>
                        )}
                        <iframe
                            ref={iframeRef}
                            src={iframeSrc}
                            title="Signals Dashboard"
                            onLoad={() => { iframeLoadedRef.current = true; setIframeLoaded(true); }}
                            style={{
                                width: isScaled ? `${DASH_W}px` : '100%',
                                height: `${DASH_H}px`,
                                border: 'none',
                                borderRadius: '4px',
                                display: iframeError ? 'none' : 'block',
                                transform: isScaled ? `scale(${scale})` : 'none',
                                transformOrigin: 'top left',
                            }}
                        />
                    </div>
                </div>
            </main>

            <footer className="models-footer">
                <div className="container">
                    <p>&copy; 2023-{new Date().getFullYear()} machinaquanta.com. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default SignalsDashboard;
